import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Lock, AlertCircle, Eye, EyeOff, RefreshCw, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const { login } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailNotVerified(false);
    setResendSuccess(false);
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      const msg = err.message || '';
      if (msg.toLowerCase().includes('not verified') || msg.toLowerCase().includes('verification')) {
        setEmailNotVerified(true);
        setError(t('auth.emailNotVerified'));
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      const res = await fetch(`${API_URL}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      if (res.ok) setResendSuccess(true);
    } catch { /* resend is best-effort */ }
    setResendLoading(false);
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 pt-20">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1a237e] rounded-full mb-4">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 data-testid="login-title" className="text-2xl font-bold text-[#0d1442]">{t('auth.login')}</h1>
              <p className="text-slate-500 mt-2">BIZZ Europe</p>
            </div>

            <form onSubmit={handleSubmit} data-testid="login-form">
              {error && (
                <div data-testid="login-error" className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p>{error}</p>
                    {emailNotVerified && (
                      <div className="mt-2">
                        {resendSuccess ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            <span>{t('auth.resendSuccess')}</span>
                          </div>
                        ) : (
                          <button
                            type="button"
                            data-testid="resend-verification-login-btn"
                            onClick={handleResend}
                            disabled={resendLoading}
                            className="inline-flex items-center gap-1 text-[#1a237e] hover:underline font-medium"
                          >
                            <RefreshCw className={`w-3 h-3 ${resendLoading ? 'animate-spin' : ''}`} />
                            {t('auth.resendVerification')}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('auth.email')}
                </label>
                <input
                  data-testid="login-email-input"
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a237e] focus:border-transparent transition-all"
                  required
                  autoFocus
                />
              </div>

              <div className="mb-6">
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <input
                    data-testid="login-password-input"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="********"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a237e] focus:border-transparent transition-all pr-12"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <div className="mt-2 text-right">
                  <Link
                    data-testid="forgot-password-link"
                    to="/forgot-password"
                    className="text-sm text-[#1a237e] hover:underline"
                  >
                    {t('auth.forgotPassword')}
                  </Link>
                </div>
              </div>

              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#1a237e] text-white font-medium rounded-lg hover:bg-[#0d1442] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? '...' : t('auth.loginButton')}
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-500">
              {t('auth.noAccount')}{' '}
              <Link data-testid="register-link" to="/register" className="text-[#1a237e] font-medium hover:underline">
                {t('auth.signUp')}
              </Link>
            </div>

            <div className="mt-4 text-center">
              <Link to="/" className="text-sm text-slate-500 hover:text-[#1a237e] transition-colors">
                {t('auth.backToWebsite')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
